import type { Alpine } from "alpinejs";

type MenuValue = "" | "item" | "submenu";

type PopoverElement = HTMLElement & {
  togglePopover?: (options: { force: boolean }) => void;
};

/*
 * Defines the closure delay so that traversal from a menu to a submenu does not
 * dismiss the submenu before the pointer arrives.
 */
const SUBMENU_CLOSE_DELAY_MS = 100;

/*
 * A menu that scrolls cuts its submenus off at its own edge. The top layer
 * draws a submenu outside that edge while leaving it where it is in the page,
 * so its links still reach the menu through closest(), with menu:item-clicked
 * still bubbling up. Browsers without this method keep the earlier behavior.
 */
function setSubmenuTopLayer(submenu: PopoverElement | null, shown: boolean) {
  submenu?.togglePopover?.({ force: shown });
}

export default (
  el: HTMLElement,
  { value }: { value: MenuValue },
  { Alpine }: { Alpine: Alpine }
) => {
  if (!value) {
    handleRoot(el, Alpine);
  } else if (value === "item") {
    handleItem(el, Alpine);
  } else if (value === "submenu") {
    handleSubmenu(el, Alpine);
  }

  function handleRoot(el: HTMLElement, Alpine: Alpine) {
    Alpine.bind(el, {
      "x-on:keydown.down.stop.prevent"() {
        this.$focus.wrap().next();
      },
      "x-on:keydown.up.stop.prevent"() {
        this.$focus.wrap().previous();
      },
      "x-on:keydown.home"() {
        this.$focus.first();
      },
      "x-on:keydown.end"() {
        this.$focus.last();
      },
    });
  }

  function handleItem(el: HTMLElement, Alpine: Alpine) {
    Alpine.bind(el, {
      "x-data"() {
        return {
          submenuOpen: false,
          submenuAnchored: false,
          submenuCloseTimer: null as ReturnType<typeof setTimeout> | null,

          cancelSubmenuClose() {
            if (this.submenuCloseTimer === null) return;
            clearTimeout(this.submenuCloseTimer);
            this.submenuCloseTimer = null;
          },

          submenuElement(): PopoverElement | null {
            return document.getElementById(
              this.$id("nested-menu")
            ) as PopoverElement | null;
          },

          openSubmenu() {
            this.cancelSubmenuClose();
            this.submenuOpen = true;
          },

          closeSubmenu() {
            this.cancelSubmenuClose();
            setSubmenuTopLayer(this.submenuElement(), false);
            this.submenuOpen = false;
            this.submenuAnchored = false;
          },

          closeSubmenuAfterDelay() {
            this.cancelSubmenuClose();
            this.submenuCloseTimer = setTimeout(() => {
              this.submenuCloseTimer = null;
              this.closeSubmenu();
            }, SUBMENU_CLOSE_DELAY_MS);
          },

          destroy() {
            this.cancelSubmenuClose();
          },
        };
      },
      "x-id"() {
        return ["nested-menu"];
      },
      "x-bind:aria-expanded"() {
        return this.submenuOpen ? "true" : "false";
      },
      "x-on:mouseleave"() {
        this.closeSubmenuAfterDelay();
      },
      "x-on:mouseover"() {
        this.openSubmenu();
        anchorSubmenu(this);
      },
      "x-on:keydown.right"() {
        this.openSubmenu();
        anchorSubmenu(this);
      },
      "x-on:keydown.enter"() {
        if (this.$event.target === this.$el) {
          this.openSubmenu();
          anchorSubmenu(this);
        }
      },
      "x-on:keydown.space"() {
        this.openSubmenu();
        anchorSubmenu(this);
      },
    });
  }

  function handleSubmenu(el: HTMLElement, Alpine: Alpine) {
    Alpine.bind(el, {
      "x-bind:style"() {
        return {
          visibility: this.submenuAnchored ? "visible" : "hidden",
        };
      },
      "x-trap"() {
        return this.submenuOpen;
      },
      "x-bind:id"() {
        return this.$id("nested-menu");
      },
      "x-on:mouseover"() {
        this.openSubmenu();
      },
      "x-on:mouseleave"() {
        this.closeSubmenu();
      },
      "x-on:keydown.escape"() {
        this.closeSubmenu();
      },
      "x-on:keydown.tab"() {
        this.closeSubmenu();
      },
      "x-on:keydown.left"() {
        if (this.$event.target.closest('[role="menu"]') == this.$el) {
          this.closeSubmenu();
        }
      },
      "x-on:menu:item-clicked"() {
        this.closeSubmenu();
      },
    });
  }

  function anchorSubmenu(context: any) {
    const submenu = document.getElementById(context.$id("nested-menu"));

    if (!submenu) return;

    setSubmenuTopLayer(submenu as PopoverElement, true);

    context.$anchor(context.$el, submenu, "right-start", "fixed");

    requestAnimationFrame(() => {
      if (context.submenuOpen) {
        context.submenuAnchored = true;
      }
    });
  }
};
